import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check, Loader2, Pause, Play } from "lucide-react";

// WO#9 / My Music V1 — format a seconds duration as m:ss.
export const formatDuration = (secs) => {
    if (typeof secs !== "number" || !isFinite(secs) || secs < 0) return "--:--";
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
};

/**
 * SHARED music track list + single-player PREVIEW engine. Reused by the Dashboard
 * MusicPicker AND the first-run template wizard so there is exactly ONE music
 * preview implementation (no duplication, no drift).
 *
 * Behavior:
 *  - Every row shows a play/pause preview button, the track name, and duration.
 *  - Only ONE preview plays at a time — starting a second stops the first.
 *  - Previewing a track NEVER selects it; selecting a track NEVER auto-plays it.
 *  - A preview continues until: the customer pauses it, another preview starts,
 *    the track naturally ends (`ended`), a real playback error fires (`error`),
 *    or the parent unmounts the engine (wizard step-change/closure, Dashboard
 *    music toggle-off, resetSignal, or the active track leaves the list).
 *  - Cold-start hardening for mobile Chromium: preload="auto" on the
 *    source-less audio element (buffers only AFTER a src is assigned); on a
 *    Play or a switch, the engine pauses the old source, assigns the new src,
 *    calls load() once, and plays. NO removeAttribute("src")+load reset
 *    immediately before assigning the replacement src. `waiting` and `stalled`
 *    are NOT handled — they are transient buffer signals, not stop signals.
 *  - Preview gain is decoupled from `music_volume` (which controls the finished-
 *    video mix only). Previews play at the audio element's default volume.
 */
export const MusicTrackList = ({
    tracks = [],
    selectedId = null,
    onSelect,
    hasMore = false,
    onLoadMore,
    loadingMore = false,
    resetSignal = 0,
    listTestId = "music-track-list",
    rowTestIdPrefix = "music-track",
    previewTestIdPrefix = "music-preview",
    loadMoreTestId = "music-load-more",
    ariaLabel = "Music track",
}) => {
    const [playingId, setPlayingId] = useState(null);
    const audioRef = useRef(null);

    // FULL cleanup: pause + drop src + load. Reserved for explicit stop
    // (customer pauses the currently-playing track), selection, reset, missing
    // track cleanup, and unmount. Never called mid-switch — see startPreview.
    const stopPreview = useCallback(() => {
        const el = audioRef.current;
        if (el) {
            try { el.pause(); el.removeAttribute("src"); el.load(); } catch (_e) { /* noop */ }
        }
        setPlayingId(null);
    }, []);

    // Guaranteed stop on unmount. The element is captured at mount so the pause
    // still fires even after React detaches the ref during teardown (wizard
    // step-change / closure, or Dashboard music toggle-off).
    useEffect(() => {
        const el = audioRef.current;
        return () => {
            if (el) {
                try { el.pause(); el.removeAttribute("src"); el.load(); } catch (_e) { /* noop */ }
            }
        };
    }, []);

    // Stop when the parent signals a reset (e.g. a library refresh).
    useEffect(() => {
        if (resetSignal) stopPreview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetSignal]);

    // Stop if the currently-playing track is no longer present in the list.
    useEffect(() => {
        if (playingId && !tracks.some((t) => t.userMediaId === playingId)) stopPreview();
    }, [tracks, playingId, stopPreview]);

    // Cold-start hardening: switching tracks pauses the OLD source, assigns the
    // NEW src, calls load once, and plays. We do NOT perform a full
    // removeAttribute("src") + load cleanup immediately before assigning the
    // replacement src — some mobile Chromium builds treated that as an aborted
    // fetch that later paused the replacement after only a couple of seconds.
    // Bounded lifecycle hardening (see FINAL REPORT §A); does NOT claim a
    // source-proven root cause.
    const startPreview = (track) => {
        const el = audioRef.current;
        if (!el || !track.url) return;               // no URL -> not playable
        try {
            el.pause();
            el.src = track.url;
            el.load();
            const p = el.play();
            if (p && p.catch) p.catch(() => setPlayingId(null)); // "Preview unavailable"
            setPlayingId(track.userMediaId);
        } catch (_e) {
            setPlayingId(null);
        }
    };

    const togglePreview = (track) => {
        if (playingId === track.userMediaId) { stopPreview(); return; }
        startPreview(track);
    };

    // Selecting a track NEVER auto-plays it (and stops any active preview).
    const selectTrack = (track) => {
        stopPreview();
        onSelect?.(track.userMediaId);
    };

    return (
        <div
            role="radiogroup"
            aria-label={ariaLabel}
            data-testid={listTestId}
            className="flex max-h-72 flex-col gap-2 overflow-y-auto pr-1"
        >
            {/* single hidden audio element — one preview at a time. preload="auto"
                on the source-less element: browsers only buffer AFTER a src is
                assigned (i.e. after the customer presses Play), so this does not
                cause background fetching, but it prevents the aggressive
                "download the smallest possible buffer" behavior some mobile
                Chromium versions apply with preload="none". `waiting` / `stalled`
                are intentionally NOT handled so a brief buffer under-run never
                clears playback state or forces a pause. */}
            <audio
                ref={audioRef}
                preload="auto"
                onEnded={() => setPlayingId(null)}
                onError={() => setPlayingId(null)}
                className="hidden"
            />

            {tracks.map((t) => {
                const isSel = selectedId === t.userMediaId;
                const isPlaying = playingId === t.userMediaId;
                return (
                    <div
                        key={t.userMediaId}
                        role="radio"
                        aria-checked={isSel}
                        tabIndex={0}
                        data-testid={`${rowTestIdPrefix}-${t.userMediaId}`}
                        onClick={() => selectTrack(t)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                selectTrack(t);
                            }
                        }}
                        className={[
                            "flex cursor-pointer items-center gap-3 rounded-lg border p-2.5",
                            "transition-[border-color,background-color] duration-200",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60",
                            isSel
                                ? "border-cyan-300/70 bg-cyan-300/10"
                                : "border-white/10 hover:border-cyan-300/40",
                        ].join(" ")}
                    >
                        <button
                            type="button"
                            data-testid={`${previewTestIdPrefix}-${t.userMediaId}`}
                            aria-label={isPlaying ? "Pause preview" : "Play preview"}
                            disabled={!t.url}
                            title={t.url ? undefined : "Preview unavailable"}
                            onClick={(e) => { e.stopPropagation(); togglePreview(t); }}
                            className="flex h-11 w-11 flex-none items-center justify-center rounded-full border border-white/15 text-white/80 transition-colors duration-200 hover:border-cyan-300/60 hover:text-white disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                        >
                            {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </button>
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-white/90">
                                {t.fileName}
                            </p>
                            <p className="text-xs text-white/45">
                                {formatDuration(t.duration)}
                                {!t.url && <span className="ml-2 italic">Preview unavailable</span>}
                            </p>
                        </div>
                        {isSel && <Check className="h-4 w-4 flex-none text-cyan-300" />}
                    </div>
                );
            })}

            {hasMore && (
                <button
                    type="button"
                    data-testid={loadMoreTestId}
                    onClick={onLoadMore}
                    disabled={loadingMore}
                    className="mt-1 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-white/12 text-sm font-semibold text-white/75 transition-colors duration-200 hover:border-cyan-300/50 hover:text-white disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                >
                    {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Load more
                </button>
            )}
        </div>
    );
};

export default MusicTrackList;
